<script setup>
import { computed } from 'vue';
import { User, AlertCircle, Sparkles } from 'lucide-vue-next';
import { marked } from 'marked';

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
  isTyping: {
    type: Boolean,
    default: false,
  },
});

const isUser = computed(() => props.message.role === 'user');
const isError = computed(() => props.message.isError);

// Configure marked for safe rendering
marked.setOptions({
  breaks: true,
  gfm: true,
});

const renderedContent = computed(() => {
  if (props.isTyping) {
    return '';
  }
  try {
    return marked.parse(props.message.content || '');
  } catch {
    return props.message.content || '';
  }
});

const formattedTime = computed(() => {
  if (!props.message.createdAt) return '';
  const date = new Date(props.message.createdAt);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
});

const totalTokens = computed(() => {
  const p = props.message.promptTokens || 0;
  const c = props.message.completionTokens || 0;
  return p + c;
});

const formattedCost = computed(() => {
  const cost = props.message.costUsd;
  if (cost == null) return null;
  if (cost < 0.01) return `$${cost.toFixed(4)}`;
  return `$${cost.toFixed(2)}`;
});
</script>

<template>
  <div class="flex gap-4 py-5 fade-in" :class="isUser ? 'bg-transparent' : 'bg-transparent'">
    <!-- Avatar -->
    <div
      class="shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
      :class="isUser ? 'bg-primary/80' : isError ? 'bg-red-500/20' : 'bg-primary/20'"
    >
      <User v-if="isUser" :size="16" class="text-white" />
      <AlertCircle v-else-if="isError" :size="16" class="text-red-400" />
      <Sparkles v-else :size="16" class="text-primary" />
    </div>

    <!-- Content -->
    <div class="flex-1 min-w-0 pt-0.5">
      <!-- Role label -->
      <div class="flex items-center gap-2 mb-1.5">
        <span class="text-sm font-semibold text-text-primary">
          {{ isUser ? 'You' : 'Ciena AI' }}
        </span>
        <span v-if="formattedTime && !isTyping" class="text-xs text-text-muted">
          {{ formattedTime }}
        </span>
        <span
          v-if="message.retrievedCount && !isTyping"
          class="text-xs text-primary/80 bg-primary/10 px-2 py-0.5 rounded-full"
        >
          {{ message.retrievedCount }} sources
        </span>
        <span
          v-if="message.model && !isTyping"
          class="text-xs text-text-muted bg-surface-secondary px-2 py-0.5 rounded-full"
        >
          {{ message.model }}
        </span>
        <span
          v-if="totalTokens && !isTyping"
          class="text-xs text-text-muted bg-surface-secondary px-2 py-0.5 rounded-full"
        >
          {{ totalTokens.toLocaleString() }} tokens
        </span>
        <span
          v-if="formattedCost && !isTyping"
          class="text-xs text-text-muted bg-surface-secondary px-2 py-0.5 rounded-full"
        >
          {{ formattedCost }}
        </span>
      </div>

      <!-- Message content -->
      <div v-if="isTyping" class="flex items-center gap-3 text-text-secondary">
        <span class="typing-dots">
          <span></span>
          <span></span>
          <span></span>
        </span>
        <span class="text-sm">Generating response...</span>
      </div>
      <div
        v-else
        class="message-content text-text-primary"
        :class="{ 'text-red-400': isError }"
        v-html="renderedContent"
      />
    </div>
  </div>
</template>

<style scoped>
.typing-dots {
  display: inline-flex;
  gap: 3px;
  align-items: center;
}

.typing-dots span {
  width: 6px;
  height: 6px;
  background-color: var(--color-primary);
  border-radius: 50%;
  animation: bounce 1.4s infinite ease-in-out both;
}

.typing-dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.typing-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

.typing-dots span:nth-child(3) {
  animation-delay: 0s;
}

@keyframes bounce {
  0%,
  80%,
  100% {
    transform: scale(0.6);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
<style lang="scss" scoped>
@tailwind base;
@tailwind components;
@tailwind utilities;
@import './../main.css';
</style>
